import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  List,
  ListItem,
  Avatar,
  Typography,
  Stack,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  Fade,
  Badge,
  LinearProgress,
  InputAdornment,
  Popover,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import FileSearchPanel from '../common/file/FileSearchPanel';
import * as fileApi from '../../lib/api/file';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ReconnectingIcon from '@mui/icons-material/Sync';
import ConnectedIcon from '@mui/icons-material/Wifi';
import DisconnectedIcon from '@mui/icons-material/WifiOff';
import GetAppIcon from '@mui/icons-material/GetApp';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import TagIcon from '@mui/icons-material/Tag';
import MenuIcon from '@mui/icons-material/Menu';
import CrownIcon from '@mui/icons-material/WorkspacePremium';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CircleIcon from '@mui/icons-material/Circle';

import client from '../../lib/api/client';
import ChatBubble from './ChatBubble';
import ChatImage from './ChatImage';
import BotResponseRenderer from './BotResponseRenderer';
import MentionInput from './MentionInput';
import { renderMessageWithMentions, isMentionedMe } from '../../utils/mentionUtils';
import { isImageFile } from '../../utils/fileKind';
import {
  MessageHoverWrapper,
  InlineEditInput,
  ReplyPreviewBanner,
  ReplyQuote,
  EmojiReactions,
} from './MessageActions';

// ─── 상수 ─────────────────────────────────────────────────────────────────────
const CONNECTION_STATUS = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
};

// ─── 유틸 ─────────────────────────────────────────────────────────────────────
const getFileIcon = (fileName, fileType) => {
  if (!fileName) return <DescriptionIcon />;
  const ext = fileName.toLowerCase().split('.').pop();
  const type = fileType?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext) || type?.startsWith('image/'))
    return <ImageIcon sx={{ color: '#4caf50' }} />;
  if (ext === 'pdf' || type === 'application/pdf')
    return <PictureAsPdfIcon sx={{ color: '#f44336' }} />;
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext))
    return <DescriptionIcon sx={{ color: '#2196f3' }} />;
  return <FolderIcon sx={{ color: '#757575' }} />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
};

// 검색어 하이라이트 (regex 특수문자 안전 처리)
const highlightText = (text, kw) => {
  if (!kw || !kw.trim() || !text) return text;
  const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp('(' + escaped + ')', 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === kw.toLowerCase()
      ? <mark key={i} style={{ background: '#fff176', borderRadius: 2, padding: '0 1px' }}>{part}</mark>
      : part
  );
};

// ─── 온라인 상태 뱃지 ─────────────────────────────────────────────────────────
const OnlineBadge = ({ online }) => (
  <CircleIcon sx={{
    fontSize: 10,
    color: online ? '#2eb67d' : '#aaa',
    position: 'absolute', bottom: -1, right: -1,
    bgcolor: '#fff', borderRadius: '50%',
  }} />
);

// ─── 검색 패널 (우측 슬라이드) ────────────────────────────────────────────────
const SearchPanel = ({ open, onClose, chatRoomUuid, onScrollToMessage }) => {
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  // 채널 바뀌면 초기화
  useEffect(() => {
    setKeyword('');
    setResults([]);
  }, [chatRoomUuid]);

  const doSearch = useCallback(async (kw) => {
    if (!kw || !kw.trim() || !chatRoomUuid) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await client.get(`/api/v1/messages/search/${chatRoomUuid}`, {
        params: { keyword: kw.trim() },
      });
      setResults(Array.isArray(res.data) ? res.data : []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [chatRoomUuid]);

  const handleChange = (e) => {
    const kw = e.target.value;
    setKeyword(kw);
    clearTimeout(timerRef.current);
    if (!kw.trim()) { setResults([]); return; }
    timerRef.current = setTimeout(() => doSearch(kw), 350);
  };

  const handleClose = () => {
    setKeyword('');
    setResults([]);
    onClose();
  };

  return (
    <Box sx={{
      width: open ? 300 : 0,
      minWidth: open ? 300 : 0,
      overflow: 'hidden',
      transition: 'width 0.22s ease, min-width 0.22s ease',
      borderLeft: open ? '1px solid #e0e0e0' : 'none',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#fafafa',
      flexShrink: 0,
    }}>
      {open && (
        <>
          {/* 패널 헤더 */}
          <Box sx={{
            px: 2, py: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1px solid #eeeeee',
            bgcolor: '#fff',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon sx={{ fontSize: 16, color: '#1976d2' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a1a' }}>
                메시지 검색
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleClose} sx={{ color: '#999' }}>
              <CloseIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Box>

          {/* 검색 입력 */}
          <Box sx={{ px: 1.5, py: 1.5, borderBottom: '1px solid #eeeeee', bgcolor: '#fff' }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="검색어를 입력하세요..."
              value={keyword}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 15, color: '#aaa' }} />
                  </InputAdornment>
                ),
                endAdornment: keyword ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setKeyword(''); setResults([]); }}>
                      <CloseIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.83rem', borderRadius: 2 } }}
            />
            {keyword.trim() && (
              <Typography sx={{ mt: 0.75, fontSize: '0.71rem', color: '#888' }}>
                {loading ? '검색 중...' : `${results.length}건의 결과`}
              </Typography>
            )}
          </Box>

          {/* 결과 목록 */}
          <Box sx={{
            flexGrow: 1, overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#e0e0e0', borderRadius: 2 },
          }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress size={22} />
              </Box>
            )}

            {!loading && !keyword.trim() && (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <SearchIcon sx={{ fontSize: 38, color: '#ddd', display: 'block', mx: 'auto', mb: 1 }} />
                <Typography sx={{ fontSize: '0.78rem', color: '#ccc' }}>검색어를 입력하세요</Typography>
              </Box>
            )}

            {!loading && keyword.trim() && results.length === 0 && (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#bbb' }}>검색 결과가 없습니다</Typography>
              </Box>
            )}

            {!loading && results.map((msg) => (
              <Box
                key={msg.messageUuid}
                onClick={() => onScrollToMessage(msg.messageUuid)}
                sx={{
                  px: 2, py: 1.5,
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  bgcolor: '#fff',
                  '&:hover': { bgcolor: 'rgba(25,118,210,0.04)' },
                  transition: 'background 0.13s',
                }}
              >
                {/* 발신자 + 날짜 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.6 }}>
                  <Avatar sx={{
                    width: 22, height: 22, fontSize: '0.63rem',
                    bgcolor: '#607d8b', flexShrink: 0,
                  }}>
                    {(msg.usrNm || msg.crtNm || '?').charAt(0)}
                  </Avatar>
                  <Typography sx={{ fontSize: '0.77rem', fontWeight: 600, color: '#333', flexGrow: 1 }} noWrap>
                    {msg.usrNm || msg.crtNm}
                  </Typography>
                  <Typography sx={{ fontSize: '0.67rem', color: '#bbb', flexShrink: 0 }}>
                    {msg.crtDt?.substring(5, 16)}
                  </Typography>
                </Box>
                {/* 내용 미리보기 (검색어 하이라이트) */}
                <Typography component="div" sx={{
                  fontSize: '0.79rem', color: '#555', lineHeight: 1.45,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  {highlightText(msg.content, keyword)}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

// ─── 멤버 팝업 ────────────────────────────────────────────────────────────────
const MemberPopup = ({
  anchorEl, open, onClose,
  participants, onlineUuids, currentUser, myRole,
  onAddMember, onRemoveMember, onTransferOwner,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTarget, setMenuTarget] = useState(null);
  const [search, setSearch] = useState('');

  const owners = participants.filter(p => p.ruleTyp === 'OWNER');
  const members = participants.filter(p => p.ruleTyp !== 'OWNER');
  const isOwnerOrAdmin = myRole === 'OWNER' || myRole === 'ADMIN';
  const filterFn = (p) => !search || p.usrNm?.toLowerCase().includes(search.toLowerCase());

  const handleContextMenu = (e, p) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuTarget(p);
  };
  const handleMenuClose = () => { setMenuAnchor(null); setMenuTarget(null); };

  const MemberRow = ({ p }) => {
    const isOnline = onlineUuids.includes(p.usrUuid);
    const isMe = p.usrUuid === currentUser;
    const isOwner = p.ruleTyp === 'OWNER';
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 1.5, py: 0.8, mx: 0.5, borderRadius: 1.5,
        bgcolor: isMe ? 'rgba(25,118,210,0.05)' : 'transparent',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
        '&:hover .member-action': { opacity: 1 },
        cursor: 'default',
      }}>
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: '0.78rem', fontWeight: 700, bgcolor: isOwner ? '#1164a3' : '#607d8b' }}>
            {p.usrNm?.charAt(0) || '?'}
          </Avatar>
          <OnlineBadge online={isOnline} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: '0.83rem', fontWeight: isMe ? 600 : 400, color: '#1a1a1a', lineHeight: 1.3 }} noWrap>
              {p.usrNm || p.usrUuid}
            </Typography>
            {isMe && <Chip label="나" size="small" sx={{ height: 16, fontSize: '0.62rem', px: 0.5, bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 700 }} />}
            {isOwner && <CrownIcon sx={{ fontSize: 13, color: '#f59e0b' }} />}
          </Box>
          <Typography sx={{ fontSize: '0.7rem', color: isOnline ? '#2eb67d' : '#aaa', lineHeight: 1 }}>
            {isOnline ? '온라인' : '오프라인'}
          </Typography>
        </Box>
        {isOwnerOrAdmin && !isMe && !isOwner && (
          <IconButton className="member-action" size="small" onClick={(e) => handleContextMenu(e, p)}
            sx={{ opacity: 0, transition: 'opacity 0.15s', color: '#999', p: 0.3, '&:hover': { color: '#e53935', bgcolor: 'rgba(229,57,53,0.08)' } }}>
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <>
      <Popover open={open} anchorEl={anchorEl} onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 280, maxHeight: 480, borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}>
        <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a1a' }}>
              멤버 {participants.length}명
              {onlineUuids.length > 0 && (
                <Typography component="span" sx={{ fontSize: '0.75rem', color: '#2eb67d', ml: 1, fontWeight: 400 }}>
                  ● {onlineUuids.length}명 온라인
                </Typography>
              )}
            </Typography>
            {isOwnerOrAdmin && (
              <Tooltip title="멤버 초대">
                <IconButton size="small" onClick={() => { onClose(); onAddMember?.(); }}
                  sx={{ bgcolor: '#1976d2', color: '#fff', width: 28, height: 28, '&:hover': { bgcolor: '#1565c0' } }}>
                  <PersonAddIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <TextField size="small" fullWidth placeholder="멤버 검색..."
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: '#aaa' }} /></InputAdornment>,
              endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><CloseIcon sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
            }}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem', borderRadius: 1.5, bgcolor: '#f8f9fa', '& fieldset': { borderColor: '#e8e8e8' } } }}
          />
        </Box>
        <Box sx={{ overflowY: 'auto', py: 1, flexGrow: 1, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#e0e0e0', borderRadius: 2 } }}>
          {owners.filter(filterFn).length > 0 && (
            <>
              <Typography sx={{ px: 2, py: 0.5, fontSize: '0.67rem', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.07em' }}>소유자</Typography>
              {owners.filter(filterFn).map(p => <MemberRow key={p.usrUuid} p={p} />)}
            </>
          )}
          {owners.filter(filterFn).length > 0 && members.filter(filterFn).length > 0 && <Divider sx={{ my: 1, mx: 2 }} />}
          {members.filter(filterFn).length > 0 && (
            <>
              <Typography sx={{ px: 2, py: 0.5, fontSize: '0.67rem', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.07em' }}>멤버 — {members.filter(filterFn).length}</Typography>
              {members.filter(filterFn).map(p => <MemberRow key={p.usrUuid} p={p} />)}
            </>
          )}
          {participants.filter(filterFn).length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.8rem', color: '#aaa' }}>{search ? '검색 결과 없음' : '멤버 정보 없음'}</Typography>
            </Box>
          )}
        </Box>
        {!isOwnerOrAdmin && (
          <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #f0f0f0' }}>
            <Typography sx={{ fontSize: '0.73rem', color: '#bbb', textAlign: 'center' }}>멤버 관리는 채널 소유자만 가능합니다</Typography>
          </Box>
        )}
      </Popover>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}
        PaperProps={{ sx: { minWidth: 180, borderRadius: 1.5, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', border: '1px solid #f0f0f0' } }}>
        {myRole === 'OWNER' && (
          <MenuItem onClick={() => { onTransferOwner?.(menuTarget); handleMenuClose(); onClose(); }}
            sx={{ fontSize: '0.85rem', color: '#f59e0b', gap: 1, '&:hover': { bgcolor: 'rgba(245,158,11,0.07)' } }}>
            <CrownIcon sx={{ fontSize: 16 }} /> 소유자 이양
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => { onRemoveMember?.(menuTarget); handleMenuClose(); onClose(); }}
          sx={{ fontSize: '0.85rem', color: '#e53935', gap: 1, '&:hover': { bgcolor: 'rgba(229,57,53,0.06)' } }}>
          <PersonRemoveIcon sx={{ fontSize: 16 }} /> 채널에서 추방
        </MenuItem>
      </Menu>
    </>
  );
};

// ─── MainchatTemplate ─────────────────────────────────────────────────────────
const MainchatTemplate = ({
  messages = [],
  onSend,
  onFileSelect,
  currentUser,
  connectionStatus = CONNECTION_STATUS.CONNECTING,
  isConnected = false,
  isTyping = false,
  unreadCount = 0,
  isAtBottom = true,
  uploadingFile = false,
  uploadProgress = 0,
  downloadingUuid = null,
  onFileDownload,
  onBotMessage,
  onScrollStateChange,
  onLoadOlderHistory,
  loadingOlderHistory = false,
  hasOlderHistory = true,
  userInfo,
  chatRoomUuid,
  channelName,
  participants = [],
  onEditMessage,
  onDeleteMessage,
  onReactMessage,
  onAddMember,
  onRemoveMember,
  onTransferOwner,
  onOpenChannelList,
  onlineUuids = [],
  myRole = 'MEMBER',
}) => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [memberAnchorEl, setMemberAnchorEl] = useState(null);
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [currentMentions, setCurrentMentions] = useState([]);
  const [replyTarget, setReplyTarget] = useState(null);
  const [editingMsgKey, setEditingMsgKey] = useState(null);
  const [attachmentAnchorEl, setAttachmentAnchorEl] = useState(null);
  // 좁은 화면(xs)에서 헤더에 모아두는 더보기 메뉴 - 연결 상태/Help를 여기로 옮긴다.
  const [headerMenuAnchorEl, setHeaderMenuAnchorEl] = useState(null);
  // 이미 올려둔 파일 찾아서 보내기
  const [fileSearchOpen, setFileSearchOpen] = useState(false);
  const [pickingFile, setPickingFile] = useState(false);
  // 봇의 FILE_LIST 응답에서 "전체 보기"를 누르면, 봇이 이미 적용한 조건을 그대로 채워 연다.
  const [fileSearchInitial, setFileSearchInitial] = useState(null);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const endRef = useRef(null);
  const fileInputRef = useRef();
  const cameraInputRef = useRef();
  const inputRef = useRef();
  const muiTheme = useTheme();
  const isNarrow = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const messagesContainerRef = useRef();
  const attachmentMenuOpen = Boolean(attachmentAnchorEl);

  // ── 메시지 가공 ────────────────────────────────────────────────────────────
  const isBotMessage = useCallback((msg) =>
    msg.crtUsr === 'BOT' || msg.usrNm === 'KMES BOT' || msg.usrNm === 'PMS BOT' || msg.isBot || msg.chatTyp === 'bot_response',
  []);

  const renderedMessages = useMemo(() =>
    messages.map((msg, index) => {
      const isBot = isBotMessage(msg);
      const isMe = msg.crtUsr === currentUser;
      const usrNm = msg.usrNm || msg.crtNm || msg.crtUsr;
      const initials = usrNm?.charAt(0)?.toUpperCase() || '?';
      // 봇 응답은 content에 JSON 원문이 들어있다. 이 파싱을 렌더 안에서 하면
      // 입력창에 글자를 칠 때마다(=매 렌더) 모든 봇 메시지를 다시 파싱하게 된다.
      // RAG 결과 카드처럼 payload가 큰 메시지가 쌓이면 타이핑이 눈에 띄게 느려져서,
      // 메시지 목록이 바뀔 때만 한 번 계산하도록 여기서 미리 만들어 둔다.
      let parsedBot = null;
      if (isBot && typeof msg.content === 'string' && msg.content.trim().startsWith('{')) {
        try {
          parsedBot = JSON.parse(msg.content.trim());
        } catch {
          parsedBot = { responseType: 'TEXT', message: msg.content };
        }
      }
      return {
        ...msg,
        isBot,
        isMe,
        usrNm,
        initials,
        parsedBot,
        key: msg.messageUuid || msg.clientTempId || `msg-${msg.crtDt || Date.now()}-${index}`,
      };
    }),
  [messages, currentUser, isBotMessage]);
  const lastRenderedMessage = renderedMessages[renderedMessages.length - 1];
  const connectedFiles = useMemo(() => {
    const result = new Map();

    renderedMessages.forEach(message => {
      if (!message.isBot) return;

      // renderedMessages 단계에서 이미 파싱해 둔 결과를 재사용한다(중복 파싱 방지).
      if (!message.parsedBot) return;
      const payload = { ...message, ...message.parsedBot };

      if (payload.responseType !== 'FILE_UPLOAD_CONNECTED') return;
      const connectedFileUuid = payload.data?.fileUuid || payload.fileUuid;
      if (connectedFileUuid) {
        result.set(connectedFileUuid, payload.data || payload);
      }
    });

    return result;
  }, [renderedMessages]);

  // ── 스크롤 ─────────────────────────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    onScrollStateChange?.(scrollHeight - scrollTop - clientHeight < 50);
  }, [onScrollStateChange]);

  // 자동 스크롤은 "새 메시지/타이핑이 도착했을 때"만 동작해야 한다.
  // isAtBottom 변화만으로 실행되면, 사용자가 위로 스크롤한 순간 isAtBottom=false로 바뀌며
  // effect가 재실행되고 마지막 메시지가 봇/내 메시지라는 이유로 다시 맨 아래로 끌려간다
  // (= 과거 이력을 볼 수 없게 되는 버그). 그래서 마지막 처리 지점을 ref로 기억해 비교한다.
  const lastAutoScrollKeyRef = useRef(null);
  // "이전 더 보기"로 과거 대화를 앞에 덧붙일 때, 덧붙이기 직전 높이를 담아둔다.
  const prevScrollHeightRef = useRef(null);

  // 과거 대화가 앞에 붙으면 그만큼 내용이 밀려 보던 위치가 위로 튄다.
  // 늘어난 높이만큼 스크롤을 내려 읽던 자리를 그대로 유지한다.
  useLayoutEffect(() => {
    const el = messagesContainerRef.current;
    const before = prevScrollHeightRef.current;
    if (!el || before == null) return;
    prevScrollHeightRef.current = null;
    const grown = el.scrollHeight - before;
    if (grown > 0) {
      el.scrollTop += grown;
    }
  }, [renderedMessages]);

  // 채팅방을 바꾸면 항상 최신 메시지 위치에서 시작한다.
  // (isAtBottom은 방이 바뀌어도 유지되므로 이 처리가 없으면 이전 방에서 위로 올려둔 상태가
  //  새 방에 그대로 적용돼 맨 위부터 보이게 된다.)
  useEffect(() => {
    lastAutoScrollKeyRef.current = null;
    const frameId = requestAnimationFrame(() => {
      endRef.current?.scrollIntoView({ block: 'end' });
    });
    return () => cancelAnimationFrame(frameId);
  }, [chatRoomUuid]);

  useEffect(() => {
    const latestKey = lastRenderedMessage?.key ?? null;
    const signature = `${latestKey}|${isTyping ? 'typing' : ''}`;
    if (signature === lastAutoScrollKeyRef.current) {
      return undefined;
    }
    lastAutoScrollKeyRef.current = signature;

    // 내가 보낸 메시지는 항상 따라 내려가고, 그 외(봇 응답·상대 메시지·타이핑)는
    // 사용자가 이미 맨 아래를 보고 있을 때만 따라간다.
    const shouldScrollToLatest = lastRenderedMessage?.isMe || isAtBottom;
    if (!shouldScrollToLatest) {
      return undefined;
    }

    const frameId = requestAnimationFrame(() => {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    });

    return () => cancelAnimationFrame(frameId);
  }, [
    isAtBottom,
    isTyping,
    lastRenderedMessage?.key,
    lastRenderedMessage?.isMe,
  ]);

  // ── 메시지 스크롤 + 하이라이트 ───────────────────────────────────────────────
  const handleScrollToMessage = useCallback((messageUuid) => {
    if (!messageUuid) return;
    const el = messagesContainerRef.current?.querySelector(`[data-msg-uuid="${messageUuid}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.style.transition = 'background-color 0.3s';
    el.style.backgroundColor = 'rgba(25, 118, 210, 0.15)';
    setTimeout(() => { el.style.backgroundColor = ''; }, 1500);
  }, []);

  // ── 전송 ───────────────────────────────────────────────────────────────────
  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    if (!text || !isConnected) return;
    const mentionUuids = currentMentions.map((m) => m.usrUuid).join(',');
    onSend?.(text, mentionUuids || null, replyTarget?.messageUuid || null, replyTarget || null);
    setInputValue('');
    setCurrentMentions([]);
    setReplyTarget(null);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [inputValue, currentMentions, isConnected, onSend, replyTarget]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && (e.nativeEvent?.isComposing || e.isComposing)) return;
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }, [handleSend]);

  // ── 수정/삭제/반응 ──────────────────────────────────────────────────────────
  const handleEditSave = useCallback((message, newContent) => {
    onEditMessage?.(message.messageUuid, newContent);
    setEditingMsgKey(null);
  }, [onEditMessage]);

  const handleDeleteMessage = useCallback((message) => {
    if (!window.confirm('메시지를 삭제하시겠습니까?')) return;
    onDeleteMessage?.(message.messageUuid);
  }, [onDeleteMessage]);

  const handleReact = useCallback((message, emoji) => {
    onReactMessage?.(message.messageUuid, emoji);
  }, [onReactMessage]);


  const handleInputChange = useCallback((value, mentions) => {
    setInputValue(value);
    if (mentions) setCurrentMentions(mentions);
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      // 이미지는 바로 올리지 않고 미리보기로 한 번 확인받는다(붙여넣기와 같은 흐름) - 내 사진이 아니거나
      // 엉뚱한 사진을 잘못 고르는 실수를 줄인다. 이미지가 아닌 파일은 기존처럼 고르면 곧바로 올린다.
      if (isImageFile(file)) {
        setPendingImage((prev) => {
          if (prev?.url) URL.revokeObjectURL(prev.url);
          return { file, url: URL.createObjectURL(file) };
        });
      } else {
        onFileSelect?.(file);
      }
      setAttachmentAnchorEl(null);
    }
    e.target.value = null;
  }, [onFileSelect]);

  // ── 클립보드 이미지 붙여넣기(Ctrl+V) / 파일 선택·카메라 촬영 공통 미리보기 ──────────
  // 이미지는 바로 올리지 않고 미리보기로 한 번 확인받는다. 클립보드는 글자를 붙여넣다가
  // 같이 딸려 온 이미지(엑셀·웹 복사 등)가 그대로 올라가는 실수를 막기 위해서이고,
  // 파일 선택자/카메라는 사용자가 고른 이미지가 맞는지 올리기 전에 한 번 더 볼 수 있게 해준다.
  // 확인하면 📎 첨부와 같은 onFileSelect 로 보내므로 업로드·제조 연결 흐름은 그대로 탄다.
  const [pendingImage, setPendingImage] = useState(null);

  const handlePaste = useCallback((event) => {
    if (uploadingFile) return;
    const items = Array.from(event.clipboardData?.items || []);
    const imageItem = items.find((item) => item.kind === 'file' && item.type.startsWith('image/'));
    if (!imageItem) return; // 글자만 있으면 평소처럼 붙여넣는다.
    const blob = imageItem.getAsFile();
    if (!blob) return;
    event.preventDefault();
    const ext = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
    // 파일명 시각은 현지 시각으로(toISOString 은 UTC 라 9시간 어긋난다).
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
    const file = new File([blob], `붙여넣은 이미지_${stamp}.${ext}`, { type: blob.type });
    setPendingImage((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return { file, url: URL.createObjectURL(file) };
    });
  }, [uploadingFile]);

  const closePendingImage = useCallback(() => {
    setPendingImage((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  const sendPendingImage = useCallback(() => {
    if (pendingImage?.file) onFileSelect?.(pendingImage.file);
    closePendingImage();
    inputRef.current?.focus?.();
  }, [pendingImage, onFileSelect, closePendingImage]);

  /**
   * 찾아낸 파일을 채팅에 보낸다.
   *
   * 내려받아서 다시 올리는 방식이다. 파일이 한 벌 더 생기지만, 기존 업로드 길을 그대로 타서
   * 채팅에는 평소 올린 파일과 똑같이 보인다. 링크만 보내면 나중에 원본이 지워졌을 때
   * 채팅에 죽은 링크가 남는다.
   */
  const handlePickExistingFile = useCallback(async (picked) => {
    if (!picked?.fileUuid) return;
    setPickingFile(true);
    try {
      const res = await fileApi.downloadFile(picked.fileUuid);
      const blob = new Blob([res.data]);
      const asFile = new File([blob], picked.fileNm || 'file', { type: blob.type });
      onFileSelect?.(asFile);
      setFileSearchOpen(false);
    } catch (e) {
      // 여기서 화면을 닫지 않는다. 닫아버리면 왜 안 됐는지 모른 채 다시 찾게 된다.
      alert('파일을 가져오지 못했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setPickingFile(false);
    }
  }, [onFileSelect]);

  /**
   * 봇이 FILE_LIST로 알려준 조건(appliedConditions)을 "올린 파일 찾기" 화면 조건으로 바꿔
   * 그 조건대로 열어준다. 채팅에는 10건까지만 보이므로, 더 보려면 같은 조건으로 전체 화면을 연다.
   */
  const handleOpenFileSearchFromConditions = useCallback((appliedConditions) => {
    setFileSearchInitial({
      query: appliedConditions?.fileQuery || '',
      uploader: appliedConditions?.uploader || '',
      fileTyp: appliedConditions?.fileTyp || '',
      strtDt: appliedConditions?.strtDt || '',
      endDt: appliedConditions?.endDt || '',
    });
    setFileSearchOpen(true);
  }, []);

  const hiddenFileInputSx = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: 1,
    height: 1,
    opacity: 0,
    pointerEvents: 'none',
  };

  const handleAttachmentMenuOpen = useCallback((event) => {
    if (uploadingFile) return;
    setAttachmentAnchorEl(event.currentTarget);
  }, [uploadingFile]);

  const handleAttachmentMenuClose = useCallback(() => {
    setAttachmentAnchorEl(null);
  }, []);

  const handleOpenFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleOpenCameraPicker = useCallback(() => {
    cameraInputRef.current?.click();
  }, []);

  const handleProjectSelect = useCallback((message) => {
    onSend?.(message);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [onSend]);

  // 새 탭으로 연다 - 채팅의 다른 링크(카드의 "상세 보기" 등, BotResponseRenderer.openChatUrl)와
  // 같은 방식이다. 같은 탭에서 이동하면 보던 대화가 사라져서 "전체 보기" 뒤에 다시 물어보기 불편하다.
  const handleNavigateRoute = useCallback((route) => {
    if (!route) return;
    window.open(`${window.location.origin}/${route}`, '_blank', 'noopener,noreferrer');
  }, []);

  // ── 연결 상태 ─────────────────────────────────────────────────────────────
  const getConnectionIcon = () => {
    switch (connectionStatus) {
      case CONNECTION_STATUS.CONNECTED: return <ConnectedIcon color="success" />;
      case CONNECTION_STATUS.RECONNECTING: return <ReconnectingIcon sx={{ animation: 'spin 1s linear infinite' }} />;
      case CONNECTION_STATUS.ERROR: return <DisconnectedIcon color="error" />;
      default: return <DisconnectedIcon color="disabled" />;
    }
  };
  const getConnectionText = () => {
    switch (connectionStatus) {
      case CONNECTION_STATUS.CONNECTED: return '연결됨';
      case CONNECTION_STATUS.RECONNECTING: return '재연결 중...';
      case CONNECTION_STATUS.ERROR: return '연결 오류';
      default: return '연결 끊김';
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  // 입력창에 글자를 칠 때마다 이 목록까지 다시 만들면 메시지가 쌓일수록 타이핑이 느려진다.
  // 목록은 메시지·편집상태 등 실제 관련 값이 바뀔 때만 다시 만든다.
  const messageItems = useMemo(() => (
            renderedMessages.map((msg, index) => {
              const parsedBotMsg = msg.parsedBot;
  
              const hasMentions = msg.mentions && msg.mentions.trim().length > 0;
              const isMentionMsg = hasMentions && isMentionedMe(msg.content, userInfo?.usrNm, msg.mentions, currentUser);
              const isEditing = editingMsgKey === msg.key;
              const botPayload = parsedBotMsg ? { ...msg, ...parsedBotMsg } : msg;
              const actionFileUuid = botPayload.data?.fileUuid || botPayload.fileUuid;
              // 서버가 이미지 업로드도 chatTyp:'file'로 보내므로, 파일명/파일타입으로 직접 판정한다.
              const isImageAttachmentMsg = (msg.chatTyp === 'file' || msg.chatTyp === 'image')
                && isImageFile({ fileNm: msg.fileNm || msg.fileName, fileType: msg.fileType });
  
              return (
                <ListItem
                  key={msg.key}
                  data-msg-index={index}
                  data-msg-uuid={msg.messageUuid}
                  sx={{
                    display: 'flex',
                    justifyContent: msg.isMe ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-start',
                    mb: 1, px: 1, py: 0.5,
                    borderRadius: 1.5,
                    bgcolor: isMentionMsg ? 'rgba(255,193,7,0.08)' : 'transparent',
                    borderLeft: isMentionMsg ? '3px solid #ffc107' : 'none',
                    transition: 'background-color 0.3s',
                  }}
                >
                  {/* 휴대폰: 봇 답변 카드는 표·버튼이 많아 85% 폭이면 너무 좁다(2026-10-15) → 남의 메시지는 거의 전체 폭 */}
                  <Stack direction="row" alignItems="flex-start" spacing={{ xs: 0.75, sm: 1 }}
                    sx={{ maxWidth: { xs: msg.isMe ? '85%' : '100%', sm: '85%' }, minWidth: 0 }}>
                    {!msg.isMe && (
                      <Stack direction="column" alignItems="center" spacing={0.5}>
                        <Avatar alt={msg.usrNm} src={msg.emoticon}
                          sx={{ bgcolor: msg.isBot ? '#2196f3' : 'grey.500', width: 32, height: 32 }}>
                          {msg.isBot ? <SmartToyIcon fontSize="small" /> : msg.initials}
                        </Avatar>
                        {/* 이름이 줄바꿈되며 왼쪽 칸을 넓게 차지했다 → 한 줄로 자르고, 휴대폰에서 봇은 아이콘만 */}
                        <Typography variant="caption" noWrap
                          sx={{ fontSize: '0.7rem', maxWidth: 56, display: msg.isBot ? { xs: 'none', sm: 'block' } : 'block' }}>
                          {msg.isBot ? 'KMES BOT' : msg.usrNm}
                        </Typography>
                      </Stack>
                    )}
  
                    <MessageHoverWrapper
                      message={msg} isMe={msg.isMe} isBot={msg.isBot}
                      canEdit={msg.isMe && !msg.isPending} canDelete={msg.isMe && !msg.isPending}
                      onReply={(m) => { setReplyTarget(m); inputRef.current?.focus(); }}
                      onEdit={(m) => setEditingMsgKey(m.key)}
                      onDelete={handleDeleteMessage}
                      onReact={onReactMessage ? handleReact : null}
                    >
                      <ChatBubble owner={msg.isMe} sx={{
                        minWidth: msg.isBot ? 0 : undefined,
                        width: msg.isBot ? 'auto' : undefined,
                        maxWidth: msg.isBot ? { xs: '100%', md: 720 } : '100%',
                        backgroundColor: msg.isBot ? '#e3f2fd' : msg.isMe ? '#1976d2' : '#f5f5f5',
                        color: msg.isBot ? '#1565c0' : msg.isMe ? 'white' : 'black',
                        border: msg.isBot ? '1px solid #bbdefb' : 'none',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        opacity: msg.isPending ? 0.72 : 1,
                      }}>
                        {msg.replyToMessage && (
                          <ReplyQuote replyToMessage={msg.replyToMessage} isMe={msg.isMe} onClickQuote={handleScrollToMessage} />
                        )}
  
                        {isImageAttachmentMsg ? (
                          // 서버는 이미지든 아니든 업로드 메시지를 항상 chatTyp:'file'로 보낸다
                          // (chatTyp:'image'는 지금 백엔드가 전혀 쓰지 않는 값이라 그 분기는 사실상
                          // 죽은 코드였다) - 그래서 파일명/파일타입으로 이미지인지 직접 판정한다.
                          <Stack spacing={0.5}>
                            <ChatImage
                              fileUuid={msg.fileUuid}
                              fileUrl={msg.fileUrl}
                              fileName={msg.fileNm || msg.fileName}
                              downloading={downloadingUuid === msg.fileUuid}
                              onDownload={() => onFileDownload?.(msg.fileUuid, msg.fileNm || msg.fileName)}
                            />
                            {msg.fileNm && <Typography variant="caption" color="text.secondary">{msg.fileNm}</Typography>}
                          </Stack>

                        ) : msg.chatTyp === 'file' ? (
                          <Stack spacing={1.5}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              {getFileIcon(msg.fileNm, msg.fileType)}
                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', wordBreak: 'break-all', lineHeight: 1.2 }}>
                                  {msg.fileNm || msg.fileName || '파일명 없음'}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  {msg.fileSize && <Typography variant="caption" color="text.secondary">{formatFileSize(msg.fileSize)}</Typography>}
                                  {msg.fileType && <Chip label={msg.fileType} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 1 } }} />}
                                </Stack>
                              </Box>
                            </Stack>
                            <Button variant="outlined" size="small" fullWidth
                              disabled={downloadingUuid === msg.fileUuid}
                              onClick={() => onFileDownload?.(msg.fileUuid, msg.fileNm || msg.fileName)}
                              startIcon={downloadingUuid === msg.fileUuid ? <CircularProgress size={16} /> : <GetAppIcon />}
                              sx={{ textTransform: 'none', borderRadius: 2 }}>
                              {downloadingUuid === msg.fileUuid ? '다운로드 중...' : '다운로드'}
                            </Button>
                          </Stack>

                        ) : msg.isBot ? (
                          <BotResponseRenderer
                            message={botPayload}
                            onProjectSelect={handleProjectSelect}
                            onBotMessage={onBotMessage}
                            alreadyLinked={Boolean(actionFileUuid && connectedFiles.has(actionFileUuid))}
                            connectedData={actionFileUuid ? connectedFiles.get(actionFileUuid) : undefined}
                            onPickExistingFile={handlePickExistingFile}
                            pickingFile={pickingFile}
                            onOpenFileSearch={handleOpenFileSearchFromConditions}
                            onNavigateRoute={handleNavigateRoute}
                          />
  
                        ) : isEditing ? (
                          <InlineEditInput
                            originalContent={msg.content || msg.text}
                            onSave={(newContent) => handleEditSave(msg, newContent)}
                            onCancel={() => setEditingMsgKey(null)}
                          />
  
                        ) : (
                          <Box>
                            {renderMessageWithMentions(msg.content || msg.text, msg.isMe)}
                            {msg.isPending && <Typography sx={{ fontSize: '0.65rem', opacity: 0.75, mt: 0.25 }}>(전송 중...)</Typography>}
                            {msg.isEdited && <Typography sx={{ fontSize: '0.65rem', opacity: 0.55, mt: 0.25 }}>(편집됨)</Typography>}
                          </Box>
                        )}
                      </ChatBubble>
  
                      {onReactMessage && msg.reactions && (
                        <EmojiReactions
                          reactions={msg.reactions}
                          onReact={(emoji) => handleReact(msg, emoji)}
                          currentUser={currentUser}
                        />
                      )}
                    </MessageHoverWrapper>
                  </Stack>
                </ListItem>
              );
            })
  ), [
    renderedMessages, userInfo, currentUser, editingMsgKey, connectedFiles,
    downloadingUuid, onFileDownload, onBotMessage,
    handleEditSave, handleDeleteMessage, handleReact, handleProjectSelect, handleScrollToMessage,
    onReactMessage, handlePickExistingFile, pickingFile, handleOpenFileSearchFromConditions,
    handleNavigateRoute,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', height: '100%', minHeight: 0, overflow: 'hidden' }}>

      {/* ── 채팅 본문 영역 ─────────────────────────────────────────────── */}
      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>

        {/* ── 채널 헤더 ──────────────────────────────────────────────────── */}
        {/* xs(휴대폰)에서는 공간이 빠듯하다 - 채널목록은 아이콘만, 멤버 수 캡션은 숨기고,
            연결 상태는 작은 점으로, Help는 더보기(⋮) 메뉴로 옮겨서 아래 "하단 상태바" 한 줄을
            통째로 없앤다(2026-10-15 모바일 레이아웃 정리). */}
        <Box sx={{
          px: { xs: 1, sm: 2 }, py: 0, minHeight: 52,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0', bgcolor: '#fff', flexShrink: 0,
          gap: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
            {onOpenChannelList && (
              <Tooltip title="채널 목록">
                {isNarrow ? (
                  <IconButton
                    size="small"
                    onClick={onOpenChannelList}
                    sx={{
                      color: 'primary.main',
                      bgcolor: 'rgba(25,118,210,0.08)',
                      borderRadius: 1.5,
                      flexShrink: 0,
                      width: 32,
                      height: 32,
                    }}
                  >
                    <MenuIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onOpenChannelList}
                    startIcon={<MenuIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      bgcolor: 'rgba(25,118,210,0.04)',
                      borderRadius: 1.5,
                      flexShrink: 0,
                      minWidth: 0,
                      height: 34,
                      px: 1.25,
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      '& .MuiButton-startIcon': {
                        mr: 0.5,
                      },
                    }}
                  >
                    채널목록
                  </Button>
                )}
              </Tooltip>
            )}
            <TagIcon sx={{ fontSize: 18, color: '#666', display: { xs: 'none', sm: 'inline-flex' } }} />
            <Typography
              noWrap
              sx={{ fontWeight: 700, fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: '#1a1a1a', minWidth: 0 }}
            >
              {channelName || '채널'}
            </Typography>
            <Tooltip title={getConnectionText()}>
              <CircleIcon sx={{
                fontSize: 10, flexShrink: 0,
                color: connectionStatus === CONNECTION_STATUS.CONNECTED ? '#2eb67d'
                  : connectionStatus === CONNECTION_STATUS.ERROR ? '#e53935' : '#f9a825',
                display: { xs: 'inline-flex', sm: 'none' },
              }} />
            </Tooltip>
            <Typography sx={{ fontSize: '0.78rem', color: '#aaa', ml: 0.5, flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
              멤버 {participants.length}명
            </Typography>
          </Box>

          {/* 헤더 우측 버튼 그룹 */}
          <Stack direction="row" spacing={{ xs: 0, sm: 0.5 }} alignItems="center" sx={{ flexShrink: 0 }}>
            {/* ★ 검색 버튼 */}
            <Tooltip title="메시지 검색">
              <IconButton size="small"
                onClick={() => setSearchPanelOpen(v => !v)}
                sx={{
                  color: searchPanelOpen ? '#1976d2' : '#666',
                  bgcolor: searchPanelOpen ? 'rgba(25,118,210,0.08)' : 'transparent',
                  borderRadius: 1.5,
                  width: { xs: 32, sm: 'auto' }, height: { xs: 32, sm: 'auto' },
                }}>
                <SearchIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* 멤버 버튼 */}
            <Tooltip title="멤버 목록">
              <IconButton size="small"
                onClick={(e) => setMemberAnchorEl(e.currentTarget)}
                sx={{
                  color: Boolean(memberAnchorEl) ? 'primary.main' : '#666',
                  bgcolor: Boolean(memberAnchorEl) ? 'rgba(25,118,210,0.08)' : 'transparent',
                  borderRadius: 1.5,
                  width: { xs: 32, sm: 'auto' }, height: { xs: 32, sm: 'auto' },
                }}>
                <Badge badgeContent={participants.length} color="primary"
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16 } }}>
                  <PeopleIcon sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* xs 전용 더보기 - 하단 상태바에 있던 연결 상태 텍스트/Help를 여기로 모은다 */}
            {isNarrow && (
              <>
                <IconButton size="small"
                  onClick={(e) => setHeaderMenuAnchorEl(e.currentTarget)}
                  sx={{ color: '#666', borderRadius: 1.5, width: 32, height: 32 }}>
                  <MoreVertIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <Menu
                  anchorEl={headerMenuAnchorEl}
                  open={Boolean(headerMenuAnchorEl)}
                  onClose={() => setHeaderMenuAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem disabled sx={{ gap: 1, fontSize: '0.8rem' }}>
                    {getConnectionIcon()}
                    {getConnectionText()}
                  </MenuItem>
                  <MenuItem
                    disabled={!isConnected}
                    onClick={() => {
                      setHeaderMenuAnchorEl(null);
                      setInputValue('help');
                      inputRef.current?.focus();
                    }}
                    sx={{ gap: 1, fontSize: '0.85rem' }}
                  >
                    ❓ Help
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        </Box>

        {/* ── 연결 상태 알림 ──────────────────────────────────────────── */}
        <Fade in={!isConnected} unmountOnExit>
          <Alert severity={connectionStatus === CONNECTION_STATUS.ERROR ? 'error' : 'warning'}
            sx={{ m: 1 }} icon={getConnectionIcon()}>
            {connectionStatus === CONNECTION_STATUS.RECONNECTING
              ? '서버와 재연결을 시도하고 있습니다...'
              : connectionStatus === CONNECTION_STATUS.ERROR
              ? '서버 연결에 문제가 발생했습니다. 페이지를 새로고침해보세요.'
              : '서버와의 연결이 끊어졌습니다. 재연결을 시도합니다...'}
          </Alert>
        </Fade>

        {/* ── 파일 업로드 진행률 ──────────────────────────────────────── */}
        <Fade in={uploadingFile} unmountOnExit>
          <Box sx={{ m: 1 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              <Stack spacing={1}>
                <Typography variant="body2">📎 파일을 업로드하고 있습니다...</Typography>
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 6, borderRadius: 3 }} />
                <Typography variant="caption" color="text.secondary">{uploadProgress}% 완료</Typography>
              </Stack>
            </Alert>
          </Box>
        </Fade>

        {/* ── 메시지 리스트 ───────────────────────────────────────────── */}
        <List ref={messagesContainerRef} onScroll={handleScroll} sx={{
          flexGrow: 1, minHeight: 0, overflowY: 'auto', p: 2,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '3px' },
          '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '3px', '&:hover': { background: '#a8a8a8' } },
        }}>
          {/* 과거 대화를 앞에 덧붙이면 그만큼 내용이 위로 밀려 화면이 튄다.
              덧붙이기 전 높이를 기억해 두었다가 늘어난 만큼 스크롤을 내려 보던 위치를 유지한다. */}
          {hasOlderHistory && (
            <ListItem sx={{ justifyContent: 'center', py: 1 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={loadingOlderHistory}
                startIcon={loadingOlderHistory ? <CircularProgress size={14} /> : null}
                onClick={() => {
                  const el = messagesContainerRef.current;
                  prevScrollHeightRef.current = el ? el.scrollHeight : null;
                  onLoadOlderHistory?.();
                }}
                sx={{ textTransform: 'none' }}
              >
                {loadingOlderHistory ? '불러오는 중…' : '이전 1주일 더 보기'}
              </Button>
            </ListItem>
          )}

          {messageItems}

          <Fade in={isTyping} unmountOnExit>
            <ListItem sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar sx={{ bgcolor: '#2196f3', width: 32, height: 32 }}><SmartToyIcon fontSize="small" /></Avatar>
                <Box sx={{ bgcolor: '#e3f2fd', borderRadius: 2, p: 1.5, border: '1px solid #bbdefb', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: '#1565c0' }} />
                  <Typography variant="body2" color="#1565c0">KMES BOT이 입력 중입니다...</Typography>
                </Box>
              </Stack>
            </ListItem>
          </Fade>

          <div ref={endRef} />
        </List>

        {/* 휴대폰에서 왼쪽 아래에 떠 있던 "채널목록" 버튼은 뺐다. 헤더에 같은 버튼이 있고, 떠 있는 버튼이
            첨부·검색 버튼을 가렸다(2026-10-08 모바일 점검). */}

        {/* ── 새 메시지 플로팅 버튼 ─────────────────────────────────── */}
        {unreadCount > 0 && !isAtBottom && (
          <Fade in>
            <Box sx={{ position: 'absolute', bottom: 100, right: 20, zIndex: 1000 }}>
              <Button variant="contained" size="small"
                onClick={() => endRef.current?.scrollIntoView({ behavior: 'smooth' })}
                sx={{ minWidth: 'auto', borderRadius: '20px', px: 2, bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}>
                <Badge badgeContent={unreadCount} color="error" sx={{ mr: 1 }}>↓</Badge>
                새 메시지
              </Button>
            </Box>
          </Fade>
        )}

        {/* ── 명령어 제안 ──────────────────────────────────────────────── */}
        {/* ── 답장 미리보기 ────────────────────────────────────────────── */}
        <ReplyPreviewBanner replyTarget={replyTarget} onCancel={() => setReplyTarget(null)} />

        {/* ── 입력창 ───────────────────────────────────────────────────── */}
        <Paper component="form" onSubmit={(e) => { e.preventDefault(); handleSend(); }}
          onPaste={handlePaste}
          sx={{
            px: { xs: 1, sm: 1.5 }, pt: 1,
            // 홈바 있는 폰에서 입력줄이 그 밑에 깔리지 않게.
            pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
            display: 'flex', alignItems: 'flex-end',
            borderTop: '1px solid #e0e0e0',
            bgcolor: isConnected ? 'white' : '#f5f5f5',
            gap: { xs: 0.5, sm: 1 }, minHeight: '60px',
          }}>
          {/* 2026-10: 서버가 허용목록 → 차단목록 방식으로 바뀌어(실행/스크립트/웹페이지 계열만 차단) STP 같은
              새 파일 형식도 업로드할 수 있다. accept는 "허용 목록"만 표현 가능해 차단목록과 맞추려면 제한을
              없애는 게 맞다 — 실제 차단은 서버 InputValidator가 한다. */}
          <input type="file" ref={fileInputRef} style={hiddenFileInputSx}
            onChange={handleFileChange} />
          <input type="file" ref={cameraInputRef} style={hiddenFileInputSx}
            onChange={handleFileChange}
            accept="image/*"
            capture="environment" />

          <Tooltip title={uploadingFile ? `업로드 중 (${uploadProgress}%)` : '파일 첨부'}>
            <span>
              <IconButton onClick={handleAttachmentMenuOpen} size={isNarrow ? 'small' : 'large'}
                disabled={uploadingFile}
                sx={{ mb: 0.5, width: { xs: 36, sm: 'auto' }, height: { xs: 36, sm: 'auto' } }}>
                {uploadingFile ? <CircularProgress size={20} /> : <AttachFileIcon fontSize={isNarrow ? 'small' : 'medium'} />}
              </IconButton>
            </span>
          </Tooltip>
          <Menu
            anchorEl={attachmentAnchorEl}
            open={attachmentMenuOpen}
            onClose={handleAttachmentMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{
              sx: {
                minWidth: 168,
                borderRadius: 2,
                boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
              },
            }}
          >
            <MenuItem onClick={handleOpenFilePicker} disabled={uploadingFile} sx={{ gap: 1.25, fontSize: '0.9rem' }}>
              <AttachFileIcon sx={{ fontSize: 19, color: '#666' }} />
              파일 선택
            </MenuItem>
            <MenuItem onClick={handleOpenCameraPicker} disabled={uploadingFile} sx={{ gap: 1.25, fontSize: '0.9rem' }}>
              <PhotoCameraIcon sx={{ fontSize: 19, color: '#666' }} />
              사진 촬영
            </MenuItem>
            {/* xs에서는 "올린 파일 찾기" 아이콘을 따로 두지 않고 이 메뉴로 접는다(공간 절약) */}
            {isNarrow && (
              <MenuItem
                onClick={() => { handleAttachmentMenuClose(); setFileSearchInitial(null); setFileSearchOpen(true); }}
                disabled={uploadingFile}
                sx={{ gap: 1.25, fontSize: '0.9rem' }}
              >
                <SearchIcon sx={{ fontSize: 19, color: '#666' }} />
                올린 파일 찾기
              </MenuItem>
            )}
          </Menu>

          {/* 이미 올려둔 파일을 찾아서 보낸다(클립은 첨부만, 찾기는 따로).
              영업·제조 어디서 올렸든 다 나온다. 같은 것을 다시 올릴 일을 줄인다.
              xs에서는 위 첨부 메뉴 안으로 접어서 이 아이콘 자체를 숨긴다. */}
          <Tooltip title="올린 파일 찾기">
            <span style={{ display: isNarrow ? 'none' : 'inline-flex' }}>
              <IconButton
                onClick={() => { setFileSearchInitial(null); setFileSearchOpen(true); }}
                size="large"
                disabled={uploadingFile}
                sx={{ mb: 0.5 }}
              >
                <SearchIcon />
              </IconButton>
            </span>
          </Tooltip>

          <MentionInput
            value={inputValue}
            onChange={handleInputChange}
            participants={participants}
            disabled={uploadingFile}
            placeholder={
              uploadingFile ? `파일 업로드 중... (${uploadProgress}%)`
              : isNarrow ? '메시지 입력' : "메시지를 입력하세요 (@로 멘션, 'help'으로 도움말)"
            }
            onKeyPress={handleKeyPress}
            inputRef={inputRef}
          />

          <Tooltip title={!inputValue.trim() ? '메시지를 입력하세요' : !isConnected ? '연결 끊김' : '전송 (Enter)'}>
            <span>
              <IconButton type="submit" color="primary"
                disabled={!inputValue.trim() || !isConnected || uploadingFile}
                sx={{
                  mb: 0.5,
                  width: { xs: 40, sm: 'auto' }, height: { xs: 40, sm: 'auto' },
                  bgcolor: inputValue.trim() && isConnected && !uploadingFile ? 'primary.main' : 'transparent',
                  color: inputValue.trim() && isConnected && !uploadingFile ? 'white' : 'inherit',
                  '&:hover': { bgcolor: inputValue.trim() && isConnected && !uploadingFile ? 'primary.dark' : 'transparent' },
                }}>
                <SendIcon fontSize={isNarrow ? 'small' : 'medium'} />
              </IconButton>
            </span>
          </Tooltip>
        </Paper>

        {/* ── 하단 상태바 (xs에서는 통째로 숨김 - 연결 상태/Help는 위 헤더로 옮겼다) ────── */}
        <Box sx={{
          px: 1.5, py: 0.75, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0',
          display: { xs: 'none', sm: 'flex' }, justifyContent: 'space-between', alignItems: 'center', minHeight: '40px',
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            💡 <strong>@멘션:</strong> @이름 입력 | <strong>KMES BOT:</strong> 'help' 입력
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip icon={getConnectionIcon()} label={getConnectionText()} size="small"
              color={isConnected ? 'success' : 'error'} variant="outlined" sx={{ fontSize: '0.7rem' }} />
            <Button size="small" variant="outlined"
              onClick={() => { setInputValue('help'); inputRef.current?.focus(); }}
              disabled={!isConnected}
              sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1, py: 0.5, textTransform: 'none',
                borderColor: 'primary.main', color: 'primary.main',
                '&:hover': { borderColor: 'primary.dark', bgcolor: 'primary.light', opacity: 0.9 } }}>
              ❓ Help
            </Button>
          </Stack>
        </Box>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </Box>

      {/* ── 검색 패널 (우측 슬라이드) ─────────────────────────────────── */}
      <SearchPanel
        open={searchPanelOpen}
        onClose={() => setSearchPanelOpen(false)}
        chatRoomUuid={chatRoomUuid}
        onScrollToMessage={handleScrollToMessage}
      />

      {/* ── 멤버 팝업 ──────────────────────────────────────────────────── */}
      <MemberPopup
        anchorEl={memberAnchorEl}
        open={Boolean(memberAnchorEl)}
        onClose={() => setMemberAnchorEl(null)}
        participants={participants}
        onlineUuids={onlineUuids}
        currentUser={currentUser}
        myRole={myRole}
        onAddMember={onAddMember}
        onRemoveMember={onRemoveMember}
        onTransferOwner={onTransferOwner}
      />

      <Dialog
        open={fileSearchOpen}
        onClose={() => { setFileSearchOpen(false); setFileSearchInitial(null); }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>올린 파일 찾기</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            영업 · 제조 어디서 올렸든 찾을 수 있습니다. 고르면 이 대화방으로 보냅니다.
          </Typography>
          <FileSearchPanel
            onPick={handlePickExistingFile}
            pickLabel={pickingFile ? '가져오는 중' : '보내기'}
            initialConditions={fileSearchInitial}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setFileSearchOpen(false); setFileSearchInitial(null); }}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 보낼 이미지 미리보기(붙여넣기 / 파일 선택 / 카메라 공통) - 보내기 버튼에 포커스가 있어
          Enter 로 보내고, Esc 로 취소(제거)한다. (따로 Enter 처리를 걸면 버튼 클릭과 겹쳐 두 번 올라간다.) */}
      <Dialog
        open={Boolean(pendingImage)}
        onClose={closePendingImage}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>이 이미지를 보낼까요?</DialogTitle>
        <DialogContent>
          {pendingImage && (
            <Stack spacing={1} alignItems="center">
              <Box
                component="img"
                src={pendingImage.url}
                alt="보낼 이미지 미리보기"
                sx={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', borderRadius: 1, border: '1px solid #e0e0e0' }}
              />
              <Typography variant="caption" color="text.secondary">
                {pendingImage.file.name} · {pendingImage.file.size >= 1024 * 1024
                  ? `${(pendingImage.file.size / 1024 / 1024).toFixed(1)}MB`
                  : `${Math.max(1, Math.round(pendingImage.file.size / 1024))}KB`}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePendingImage} startIcon={<CloseIcon />}>제거</Button>
          <Button variant="contained" onClick={sendPendingImage} autoFocus>보내기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MainchatTemplate;
